import { useActivateDealMutation } from '@src/components/OMS/ManualDealTradeView/endpoints/activateDealEndpoint';
import { modifyDeal } from '@src/components/OMS/ManualDealTradeView/ManualDealSlice';
import { openManualDealTradeFormThunk } from '@src/components/OMS/ManualDealTradeView/thunks/openManualDealTradeFormThunk';
import { ModifyDealWarningDialog } from '@src/containers/Blotters/Orders/Dialogs/ModifyDealWarningDialog';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { useAppStateDispatch } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import type { OTCDealData } from '@src/types/OTC';
import { isForwardDeal, isOptionDeal } from '@src/types/OTC/otcGuards';
import {
  notifyIfDealSecurityInvalidForModify,
  notifyIfProductTemplateMissingForModify,
} from '@src/utils/OTC/notifyModifyDealGuardFailures';
import { getOtcDealDisplayUser } from '@src/utils/OTC/otcDealDisplayUtils';
import { isDealActivatable, isDealCancelable, isDealModifiable } from '@src/utils/OTC/otcUtils';
import { validateInstantiatedDealSecurityForModify } from '@src/utils/OTC/validateInstantiatedDealSecurityForModify';
import type { OTCProductTemplateSecurity } from '@talos/kyoko';
import {
  ACTION,
  filterByCellValueMenuItem,
  getShowJSONContextItem,
  IconName,
  useCallbackRef,
  useDisclosureWithContext,
  useGetDefaultContextMenuItems,
  useGlobalToasts,
  useJsonModal,
  useSecuritiesContext,
  useUserContext,
  type AgGridIconButtonProps,
  type Column,
  type ColumnDef,
  type FilterableProperty,
  type MixpanelInstance,
  type Security,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, ICellRendererParams, MenuItemDef } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useCancelDealDialog } from '../CancelDealDialog';
import { getProductTemplateForDeal } from '../InstrumentInspectorDrawer/shared/getProductTemplateForDeal';
import { createDealJsonObject } from '../useCreateDealJsonObject';
import { OTCGridRowMenu } from './OTCGridRowMenu';
import { colIDToFilterBuilderKey } from './useOTCDerivativesFilter';

interface UseOTCMenuParams {
  openClause?: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties?: FilterableProperty[];
  mixpanel: MixpanelInstance;
  /** Opens OTC derivative instrument inspector; must be stable (e.g. from parent `useCallback`). */
  openOTCInstrumentInspectorDrawer: (deal: OTCDealData, onlyIfOpen?: boolean) => void;
}

export function useOTCDerivativesMenu({
  openClause,
  filterableProperties,
  mixpanel,
  openOTCInstrumentInspectorDrawer,
}: UseOTCMenuParams): {
  columns: Column[];
  getContextMenuItems: (params: GetContextMenuItemsParams) => (MenuItemDef | string)[];
  dialogs: ReactNode;
} {
  const { handleClickJson, jsonModal } = useJsonModal<{ deal: OTCDealData; security: Security | undefined }>();
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const dispatch = useAppStateDispatch();
  const securitiesContext = useSecuritiesContext();
  const { securitiesBySymbol } = securitiesContext;
  const { add: addToast } = useGlobalToasts();
  const { user } = useUserContext();
  const modifyDealWarningDialog = useDisclosureWithContext<OTCDealData>({});
  const handleCancelDeal = useCancelDealDialog();
  const [activateDeal] = useActivateDealMutation();
  const { isAuthorized } = useRoleAuth();
  const canSubmitDeal = isAuthorized(ACTION.SUBMIT_DEAL);
  const canCancelDeal = isAuthorized(ACTION.CANCEL_DEAL);

  const onModifyDeal = useCallback(
    (deal: OTCDealData) => {
      const dealSecurity = validateInstantiatedDealSecurityForModify(deal, securitiesBySymbol);
      if (!notifyIfDealSecurityInvalidForModify(deal, dealSecurity, addToast)) {
        return;
      }

      const productTemplate = getProductTemplateForDeal(deal, securitiesContext) as
        | OTCProductTemplateSecurity
        | undefined;
      if (!notifyIfProductTemplateMissingForModify(deal, productTemplate, addToast)) {
        return;
      }

      dispatch(modifyDeal({ deal, productTemplate }));
      // Thunk will detect deal being modified and use its booking type
      dispatch(openManualDealTradeFormThunk({}));
    },
    [dispatch, securitiesBySymbol, securitiesContext, addToast]
  );

  const createCustomJsonObject = useCallbackRef((deal?: OTCDealData) => {
    createDealJsonObject({
      deal,
      propertyName: 'deal',
      securitiesBySymbol,
      handleClickJson,
      mixpanel,
    });
  });

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<OTCDealData>) => {
    const data = params.node?.data;

    const menuItems = compact([
      ...(openClause
        ? filterByCellValueMenuItem({
            params,
            filterableProperties: filterableProperties || [],
            openClause,
            colIDToFilterBuilderKey,
            mixpanel,
          })
        : []),
      'separator',
      {
        name: 'View Instrument Details',
        action: () => {
          if (data) {
            openOTCInstrumentInspectorDrawer(data);
          }
        },
        icon: `<i class="ag-icon ${IconName.InformationCircle}"/>`,
      },
    ]);

    const isModifiable = data && isDealModifiable(data);
    const isCancelable = data && isDealCancelable(data);
    const canModifyOTCDerivative = data && isModifiable && (isForwardDeal(data) || isOptionDeal(data));
    const isActivatable = data && isDealActivatable(data) && data.ID != null;

    const secondaryActions: MenuItemDef[] = [];
    if (canModifyOTCDerivative && canSubmitDeal) {
      secondaryActions.push({
        name: 'Modify Booking',
        action: () => {
          if (data) {
            if (getOtcDealDisplayUser(data) === user.Name) {
              onModifyDeal(data);
            } else {
              modifyDealWarningDialog.open(data);
            }
          }
        },
        icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
      });
    }
    if (isCancelable && canCancelDeal) {
      secondaryActions.push({
        name: 'Cancel Deal',
        action: () => {
          handleCancelDeal(data);
        },
        icon: `<i class="ag-icon ${IconName.Trash}"/>`,
      });
    }
    if (isActivatable && canSubmitDeal) {
      secondaryActions.push({
        name: 'Activate Deal',
        action: () => {
          if (data?.ID != null) {
            void activateDeal({ dealId: data.ID });
          }
        },
        icon: `<i class="ag-icon ${IconName.CheckCircle}"/>`,
      });
    }
    if (secondaryActions.length > 0) {
      menuItems.push('separator', ...secondaryActions);
    }

    menuItems.push(
      'separator',
      getShowJSONContextItem({
        params,
        handleClickJson: createCustomJsonObject,
      }),
      'separator',
      // Default context menu items
      ...getDefaultContextMenuItems(params)
    );

    return menuItems;
  });

  const renderOTCMenuItems = useCallbackRef((params: ICellRendererParams<OTCDealData>) => (
    <OTCGridRowMenu {...params} onShowJson={createCustomJsonObject} />
  ));

  const columns = useMemo(
    () =>
      [
        // Show More Details button (pinned to the right, before hamburger menu)
        {
          type: 'iconButton',
          id: 'show-more-details',
          frozen: true,
          pinned: 'right',
          suppressColumnsToolPanel: true,
          width: 40,
          params: {
            icon: IconName.Deepdive,
            onClick: params => {
              const data: OTCDealData | undefined = params.node.data;
              if (data) {
                openOTCInstrumentInspectorDrawer(data);
              }
            },
          } satisfies AgGridIconButtonProps,
        },
        {
          type: 'hamburgerMenu',
          id: 'otcMenu',
          width: 40,
          params: {
            renderItems: renderOTCMenuItems,
          },
        },
      ] satisfies ColumnDef<OTCDealData>[],
    [openOTCInstrumentInspectorDrawer, renderOTCMenuItems]
  );

  const dialogs = useMemo(
    () => (
      <>
        {jsonModal}
        <ModifyDealWarningDialog
          disclosure={modifyDealWarningDialog}
          handleConfirm={deal => {
            onModifyDeal(deal);
          }}
        />
      </>
    ),
    [onModifyDeal, jsonModal, modifyDealWarningDialog]
  );

  return {
    columns,
    getContextMenuItems,
    dialogs,
  };
}
